import { Loader, Pause, Play, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';

import { CascadeSize } from '../../types/time-tracking';
import { StopButton } from './stop-button';
import { TimeTrackingProjectSelector } from './time-tracking-project-selector';

/**
 * CascadeView - Card-style timer panel for the expanded sidebar.
 *
 * Layout:
 * - Title row: "Time tracking" + a corner accessory button. "+" opens the
 *   add-entry modal when idle; "×" discards the running timer. Keeping
 *   discard in the title row leaves the main action row compact.
 * - Project selector: full-width bordered field variant. The dot inside
 *   the field shows the project's color when one is selected.
 * - When running: a single row with the live timer on the left and
 *   pause/resume + stop on the right.
 * - When idle: a single row with the project field, an add-entry button,
 *   and the play button.
 */
interface CascadeViewProps {
  displayTime: string;
  className?: string;
  selectedProject: DropdownSearchItem | null;
  isActive: boolean;
  isPaused: boolean;
  isLimitReached: boolean;
  /** Controls sizing tier for buttons, padding and text */
  size?: CascadeSize;

  // Loading state per action.
  isStartPending?: boolean;
  isTogglePausePending?: boolean;
  isStopPending?: boolean;
  isDiscardPending?: boolean;
  isAnyActionPending?: boolean;

  // Handlers
  openCreateModal: () => void;
  openProjectSelector: () => void;
  toggle: () => void;
  togglePause: () => void;
  reset: () => void;
}

const SIZE_CONFIG = {
  compact: {
    wrapper: 'gap-2 p-2.5',
    controlButton: 'h-8 w-8',
    controlIcon: 'h-3.5 w-3.5',
    timerText: 'text-base',
  },
  medium: {
    wrapper: 'gap-2.5 p-3',
    controlButton: 'h-9 w-9',
    controlIcon: 'h-4 w-4',
    timerText: 'text-lg',
  },
  full: {
    wrapper: 'gap-3 p-4',
    controlButton: 'h-9 w-9',
    controlIcon: 'h-4 w-4',
    timerText: 'text-2xl',
  },
} as const;

export function CascadeView({
  displayTime,
  className,
  selectedProject,
  isActive,
  isPaused,
  isLimitReached,
  isStartPending,
  isTogglePausePending,
  isStopPending,
  isDiscardPending,
  isAnyActionPending,
  openCreateModal,
  openProjectSelector,
  toggle,
  togglePause,
  reset,
  size = 'full',
}: CascadeViewProps) {
  const { t } = useTranslation(['authenticated.timeTracking', 'common']);

  const config = SIZE_CONFIG[size];
  const controlButtonClass = config.controlButton;
  const controlIconClass = config.controlIcon;
  const timerTextClass = config.timerText;

  return (
    <div
      className={cn('flex flex-col rounded-lg border bg-card shadow-sm', config.wrapper, className)}
    >
      {/* Title row — discard sits in the corner while running to keep the main
          action row compact; idle has nothing to collapse so the corner is empty. */}
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">{t('tracker.title')}</span>
        {isActive && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            onClick={reset}
            disabled={isAnyActionPending}
          >
            {isDiscardPending ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      {/* Idle: project field + add-entry + play button on a single row */}
      {!isActive && (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <TimeTrackingProjectSelector
              selectedProject={selectedProject}
              onClick={openProjectSelector}
              variant="field"
            />
          </div>
          <Button
            type="button"
            variant="default"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full"
            onClick={toggle}
            disabled={isStartPending}
          >
            {isStartPending ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <Play fill="currentColor" className="h-5 w-5" />
            )}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="icon"
            className="h-8 w-8 shrink-0 rounded-full bg-muted-foreground/15 hover:bg-muted-foreground/25"
            onClick={openCreateModal}
            disabled={isStartPending}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Running: full-width project field, then timer + controls row */}
      {isActive && (
        <>
          <TimeTrackingProjectSelector
            selectedProject={selectedProject}
            onClick={openProjectSelector}
            variant="field"
          />
          <div className="flex items-center justify-between gap-3">
            <span
              className={cn(
                'min-w-0 font-bold leading-none tabular-nums',
                timerTextClass,
                isPaused && !isLimitReached && 'text-muted-foreground'
              )}
              onClick={openCreateModal}
            >
              {displayTime}
            </span>

            <div className="flex items-center gap-1.5">
              {!isLimitReached && (
                <Button
                  type="button"
                  variant="default"
                  size="icon"
                  className={cn('rounded-full', controlButtonClass)}
                  onClick={togglePause}
                  disabled={isAnyActionPending}
                >
                  {isTogglePausePending ? (
                    <Loader className={cn('animate-spin', controlIconClass)} />
                  ) : isPaused ? (
                    <Play fill="currentColor" className={controlIconClass} />
                  ) : (
                    <Pause fill="currentColor" className={controlIconClass} />
                  )}
                </Button>
              )}
              <StopButton
                buttonClassName={controlButtonClass}
                toggle={toggle}
                isLoading={isStopPending}
                disabled={isAnyActionPending}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
